import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api.service';
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Grid,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import CodeIcon from '@mui/icons-material/Code';
import RefreshIcon from '@mui/icons-material/Refresh';

const TenantFeatures = () => {
  const { tenantAlias } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(null);
  const [featureConfig, setFeatureConfig] = useState('{}');
  const [features, setFeatures] = useState([]);

  // Debug log for params
  useEffect(() => {
    console.log('TenantFeatures params:', { tenantAlias });
  }, [tenantAlias]);

  // Fetch tenant details and features
  const fetchTenantAndFeatures = async () => {
    if (!tenantAlias) {
      console.error('No tenant alias provided');
      setError('No tenant alias provided');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching tenant info for: ${tenantAlias}`);
      
      // First, get tenant details
      const tenantResponse = await apiService.get(`/admin/developer/tenant/${tenantAlias}`);
      console.log('Tenant response:', tenantResponse);
      
      if (!tenantResponse || !tenantResponse.tenant_alias) {  
        throw new Error('Invalid tenant data received');
      }
      
      setTenant(tenantResponse);
      
      // Then, get features for this tenant using the alias
      console.log(`Fetching features for tenant alias: ${tenantAlias}`);
      const featuresResponse = await apiService.get(`/features/tenant/${tenantAlias}/features`);
      console.log('Features response:', featuresResponse);
      
      // Fetch all available features
      const allFeaturesResponse = await apiService.get('/features/featurelist');
      console.log('All Features Response:', allFeaturesResponse);
      
      // Process the data to create modules and features
      const processedModules = processFeatures(
        allFeaturesResponse, 
        featuresResponse
      );
      
      setModules(processedModules);
      
      // Initialize selected features based on what's already enabled
      const initialSelectedFeatures = {};
      processedModules.forEach(module => {
        module.features.forEach(feature => {
          initialSelectedFeatures[feature.feature_code] = feature.enabled || false;
        });
      });
      
      setSelectedFeatures(initialSelectedFeatures);
      
      // Transform features object to array for easier manipulation
      const featuresArray = Object.entries(featuresResponse.data || {}).map(([key, value]) => ({
        key,
        enabled: value === true || (typeof value === 'object' && value.enabled === true),
        config: typeof value === 'object' ? value.config || {} : {}
      }));
      
      setFeatures(featuresArray);
    } catch (err) {
      console.error('Error fetching tenant data:', err);
      setError(`Failed to load tenant data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchTenantAndFeatures();
  }, [tenantAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  // Process features data
  const processFeatures = (allFeatures, tenantFeatures) => {
    // Ensure we have valid objects to work with
    allFeatures = allFeatures || {};
    tenantFeatures = tenantFeatures || {};
    
    console.log("Processing features with:", { 
      allFeatures: JSON.stringify(allFeatures).substring(0, 100) + "...", 
      tenantFeatures: JSON.stringify(tenantFeatures).substring(0, 100) + "..." 
    });
    
    // Create a flat map of tenant features for easier lookup
    const tenantFeaturesMap = {};
    
    // Check if tenantFeatures is a flat object or has a nested structure
    if (tenantFeatures.features) {
      // Nested structure
      Object.entries(tenantFeatures.features || {}).forEach(([moduleName, moduleData]) => {
        if (moduleData.features) {
          Object.entries(moduleData.features).forEach(([featureKey, featureData]) => {
            if (featureData.feature_code) {
              tenantFeaturesMap[featureData.feature_code] = {
                enabled: featureData.enabled || false,
                config: featureData.config || {}
              };
            }
          });
        }
      });
    } else {
      // Flat structure
      Object.entries(tenantFeatures).forEach(([featureCode, value]) => {
        const isEnabled = value === true || (typeof value === 'object' && value.enabled === true);
        const config = typeof value === 'object' ? value.config || {} : {};
        
        tenantFeaturesMap[featureCode] = {
          enabled: isEnabled,
          config: config
        };
      });
    }
    
    console.log("Tenant features map:", tenantFeaturesMap);
    
    // Create modules array from all features
    const modulesMap = {};
    
    // First, process all available features
    Object.entries(allFeatures.features || {}).forEach(([moduleName, moduleData]) => {
      const moduleCode = moduleData.module_code;
      
      if (!modulesMap[moduleCode]) {
        modulesMap[moduleCode] = {
          module_name: moduleName,
          module_code: moduleCode,
          features: [],
          hasEnabledFeatures: false
        };
      }
      
      // Process features for this module
      Object.entries(moduleData.features || {}).forEach(([featureKey, featureData]) => {
        // Check if this feature is enabled for the tenant using the flat map
        const tenantFeature = tenantFeaturesMap[featureData.feature_code];
        const isEnabled = tenantFeature ? tenantFeature.enabled : false;
        
        const feature = {
          feature_id: featureData.feature_id,
          feature_code: featureData.feature_code,
          feature_name: featureData.description,
          description: featureData.description,
          is_premium: featureData.is_premium || false,
          enabled: isEnabled, // Explicitly set based on tenant features
          config: tenantFeature ? tenantFeature.config : (featureData.config || {})
        };
        
        modulesMap[moduleCode].features.push(feature);
        
        // Update hasEnabledFeatures flag
        if (feature.enabled) {
          modulesMap[moduleCode].hasEnabledFeatures = true;
        }
      });
    });
    
    // Convert the map to an array and sort features within each module
    return Object.values(modulesMap).map(module => ({
      ...module,
      features: sortFeatures(module.features)
    }));
  };

  // Add this function to sort features (MODULE first, then alphabetically)
  const sortFeatures = (features) => {
    return [...features].sort((a, b) => {
      // Always put MODULE features first
      if (a.feature_code.includes('_MODULE_')) return -1;
      if (b.feature_code.includes('_MODULE_')) return 1;
      
      // Then sort alphabetically by feature_code
      return a.feature_code.localeCompare(b.feature_code);
    });
  };

  // Handle feature selection
  const handleFeatureToggle = (featureCode) => {
    setSelectedFeatures(prev => ({
      ...prev,
      [featureCode]: !prev[featureCode]
    }));
  };

  // Handle module selection (selects/deselects all features in the module)
  const handleModuleToggle = (moduleCode) => {
    const moduleFeatures = modules
      .find(m => m.module_code === moduleCode)
      .features.map(f => f.feature_code);
    
    // Check if all features in this module are currently selected
    const allSelected = moduleFeatures.every(code => selectedFeatures[code]);
    
    // Toggle all features in this module
    const newSelectedFeatures = { ...selectedFeatures };
    moduleFeatures.forEach(code => {
      newSelectedFeatures[code] = !allSelected;
    });
    
    setSelectedFeatures(newSelectedFeatures);
  };

  // Open feature configuration dialog
  const handleOpenConfigDialog = (feature) => {
    setCurrentFeature(feature);
    setFeatureConfig(JSON.stringify(feature.config || {}, null, 2));
    setConfigDialogOpen(true);
  };

  // Save feature configuration
  const handleSaveConfig = () => {
    try {
      const parsedConfig = JSON.parse(featureConfig);
      // Update the selected features with the new config
      // This would be saved when the main form is submitted
      setConfigDialogOpen(false);
    } catch (err) {
      alert('Invalid JSON configuration');
    }
  };

  // Save all feature selections
  const handleSaveFeatures = async () => {
    setSaving(true);
    try {
      const featuresObject = {};
      
      Object.entries(selectedFeatures).forEach(([featureCode, enabled]) => {
        featuresObject[featureCode] = enabled;
      });
      
      console.log(`Saving features for tenant ${tenantAlias}:`, featuresObject);
      
      // Save features using apiService
      await apiService.post(`/features/tenant/${tenantAlias}/update`, {
        features: featuresObject
      });
      
      setSuccess('Features saved successfully');
      
      // Refresh the data
      fetchTenantAndFeatures();
    } catch (err) {
      console.error('Error saving features:', err);
      setError(`Failed to save features: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading tenant features...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/developers/tenant-management')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Tenant Features
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={() => fetchTenantAndFeatures()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSaveFeatures}
            disabled={loading || saving}
            color="primary"
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      {tenant && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {tenant.tenant_name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Tenant Alias: {tenantAlias}
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        {modules.map((module) => (
          <Accordion key={module.module_code} defaultExpanded={module.hasEnabledFeatures}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={module.features.every(f => selectedFeatures[f.feature_code])}
                      indeterminate={
                        module.features.some(f => selectedFeatures[f.feature_code]) && 
                        !module.features.every(f => selectedFeatures[f.feature_code])
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModuleToggle(module.module_code);
                      }}
                    />
                  }
                  label={
                    <Typography variant="h6">
                      {module.module_name}
                    </Typography>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <Chip 
                  label={`${module.features.filter(f => selectedFeatures[f.feature_code]).length}/${module.features.length}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Divider sx={{ mb: 2 }} />
              {module.features.map((feature) => (
                <Box 
                  key={feature.feature_code} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={selectedFeatures[feature.feature_code] || false}
                          onChange={() => handleFeatureToggle(feature.feature_code)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            {feature.feature_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {feature.feature_name}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                  <Box>
                    <Tooltip title="Feature Configuration">
                      <IconButton 
                        size="small"
                        onClick={() => handleOpenConfigDialog(feature)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Feature Details">
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveFeatures}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Feature Configuration Dialog */}
      <Dialog 
        open={configDialogOpen} 
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CodeIcon sx={{ mr: 1 }} />
            Feature Configuration
          </Box>
        </DialogTitle>
        <DialogContent>
          {currentFeature && (
            <>
              <Typography variant="h6" gutterBottom>
                {currentFeature.feature_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {currentFeature.feature_code}
              </Typography>
              <TextField
                label="JSON Configuration"
                multiline
                rows={10}
                value={featureConfig}
                onChange={(e) => setFeatureConfig(e.target.value)}
                fullWidth
                variant="outlined"
                margin="normal"
                error={!isValidJson(featureConfig)}
                helperText={!isValidJson(featureConfig) ? "Invalid JSON" : ""}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveConfig} 
            variant="contained" 
            disabled={!isValidJson(featureConfig)}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper function to validate JSON
function isValidJson(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}

export default TenantFeatures;